import { z } from 'zod';
import { lotVehicleDetailSchema, lotVehicleSchema } from './vehicle';
import { conferenceInfoSchema } from './conference';
import { paginationQuerySchema } from './pagination';

/**
 * Sale aggregate shapes (M2). Mirrors the `sales` / `sale_lots` / `bids` tables in
 * `docs/03-data-model.md` and the endpoints in `docs/04-api-contract.md`. Money is
 * integer minor units (kuruş); timestamps are epoch-ms integers on the wire, matching
 * the rest of the API (e.g. `userSummary.createdAt`).
 */

export const saleStyleSchema = z.enum([
  'parallel',
  'serial',
  'serial-10',
  'serial-with-cooldown',
]);
export type SaleStyle = z.infer<typeof saleStyleSchema>;

export const saleStateSchema = z.enum([
  'DRAFT',
  'PUBLISHED',
  'LIVE',
  'PAUSED',
  'CLOSED',
  'CANCELLED',
]);
export type SaleState = z.infer<typeof saleStateSchema>;

export const lotStateSchema = z.enum([
  'SCHEDULED',
  'OPEN',
  'SOLD',
  'UNSOLD',
  'REMOVED',
]);
export type LotState = z.infer<typeof lotStateSchema>;

/**
 * Presentation status label shown in the UI (maps onto the internal {@link SaleState}):
 * `preparing`=PUBLISHED, `inprogress`=LIVE, `done`=CLOSED. For **unauthenticated**
 * viewers `PAUSED` is masked to `inprogress` (the pause is hidden from the public).
 */
export const presentationStatusSchema = z.enum([
  'draft',
  'preparing',
  'inprogress',
  'paused',
  'done',
  'cancelled',
]);
export type PresentationStatus = z.infer<typeof presentationStatusSchema>;

export const bidKindSchema = z.enum(['bid', 'early-bid', 'free-bid']);
export type BidKind = z.infer<typeof bidKindSchema>;

// --- Increment bands ("barem", M4.5) ---------------------------------------

/**
 * One price-band increment tier. `upTo` is the inclusive upper price bound in kuruş for
 * which `increment` applies; a single tail tier carries `upTo: null` (the step for every
 * price above the last bound). Bands resolve **dynamically** — the effective step is
 * looked up from the *current* price on each bid, so it changes as bands are crossed
 * (`docs/03-data-model.md`, ADR 0016).
 */
export const incrementBandSchema = z.object({
  upTo: z.number().int().positive().nullable(), // kuruş; null = top (open-ended) band
  increment: z.number().int().positive(), // kuruş; the step within this band
});
export type IncrementBand = z.infer<typeof incrementBandSchema>;

/**
 * A full barem: at least one tier, sorted strictly ascending by `upTo`, with the `null`
 * tail (if present) last and exactly once. Refined so a bad config can't produce an
 * ambiguous or gap-ordered lookup.
 */
export const incrementBandsSchema = z
  .array(incrementBandSchema)
  .min(1)
  .superRefine((bands, ctx) => {
    let sawNull = false;
    let prev = 0;
    bands.forEach((b, i) => {
      if (sawNull) {
        ctx.addIssue({ code: 'custom', message: 'the null (top) band must be last' });
        return;
      }
      if (b.upTo === null) {
        sawNull = true;
        return;
      }
      if (b.upTo <= prev) {
        ctx.addIssue({
          code: 'custom',
          message: 'bands must be sorted strictly ascending by upTo',
          path: [i, 'upTo'],
        });
      }
      prev = b.upTo;
    });
  });
export type IncrementBands = z.infer<typeof incrementBandsSchema>;

// --- Create DTO (POST /admin/sales) ----------------------------------------

/** Sale mode: `normal` (hidden reserve must be crossed) or `guaranteed` (kesin satış). */
export const saleModeSchema = z.enum(['normal', 'guaranteed']);
export type SaleMode = z.infer<typeof saleModeSchema>;

/** One lot in a create request; `reserve`/`increment`/`currency` default from the vehicle. */
export const saleLotInputSchema = z.object({
  vehicleId: z.uuid(),
  orderIndex: z.number().int().nonnegative(),
  startingPrice: z.number().int().nonnegative(), // kuruş (the only price bidders see)
  reserve: z.number().int().positive().optional(), // kuruş; hidden reserve
  saleMode: saleModeSchema.optional(), // defaults to 'normal'
  govBarcode: z.string().optional(), // government auction barcode (per-auction)
  increment: z.number().int().positive().optional(), // kuruş; flat fallback step
  // Engine v2 (M4.5): a fixed per-lot step that replaces the bands, and a different step
  // once the reserve is crossed. Both optional; omit to inherit the sale's bands.
  incrementOverride: z.number().int().positive().optional(), // kuruş; fixed step, wins over bands
  postThresholdIncrement: z.number().int().positive().optional(), // kuruş; step after reserve crossed
  currency: z.string().optional(),
});
export type SaleLotInput = z.infer<typeof saleLotInputSchema>;

export const saleCreateSchema = z.object({
  title: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  defaultReserve: z.number().int().positive().optional(), // kuruş; auction-level reserve default
  incrementBands: incrementBandsSchema.optional(), // "barem" (M4.5); omit for a flat increment
  listingDate: z.number().int(), // epoch-ms
  startDate: z.number().int(), // epoch-ms
  style: saleStyleSchema,
  jointConference: z.boolean().optional(),
  targetCompany: z.array(z.uuid()).optional(),
  lots: z.array(saleLotInputSchema).min(1),
});
export type SaleCreateInput = z.infer<typeof saleCreateSchema>;

/** `POST /admin/sales/:id/lots/:lotId/take-control` — operator opt-in take-control toggle. */
export const takeControlSchema = z.object({
  manualControl: z.boolean(),
  softCloseMs: z.number().int().positive().optional(), // ms; the soft-close window (set when enabling)
});
export type TakeControlInput = z.infer<typeof takeControlSchema>;

// --- Read projections -------------------------------------------------------

/** Lot projection: schedule + the denormalised hot-path fields the UI renders. */
export const saleLotSchema = z.object({
  id: z.uuid(),
  saleId: z.uuid(),
  orderIndex: z.number().int(),
  vehicleId: z.uuid(),
  /**
   * `null` for an anonymous viewer (M11): the plate identifies the car to its owner and to
   * anyone with a traffic-registry lookup, so it stays behind the login with the money.
   */
  plateSnapshot: z.string().nullable(),
  govBarcode: z.string().nullable(), // government auction barcode (per-auction)
  /**
   * kuruş — the only price bidders see. `null` when `priceMasked`: the masking happens
   * server-side, so the numbers never leave the Worker for an anonymous caller (ADR 0025).
   */
  startingPrice: z.number().int().nullable(),
  // The numeric reserve is hidden from bidders (ADR 0013); it is projected **only to
  // privileged sale managers** (ADMIN/DEVELOPER/EDITOR) so they can
  // inspect/adjust it. Absent for everyone else. The derived flags below are always shown.
  reserve: z.number().int().nullable().optional(),
  saleMode: saleModeSchema,
  reserveCrossed: z.boolean(), // true once the lot flipped to guaranteed sale (kesin satış)
  // The effective NEXT step at the current price (bands resolved dynamically, M4.5). What
  // a bidder's one-tap +increment will add. `null` when `priceMasked`.
  increment: z.number().int().nullable(), // kuruş
  /**
   * True when this projection is the anonymous one: every money field is `null` and the plate
   * is withheld. The client renders `x.xxx,xx ₺` from this flag — it never receives the real
   * figures to hide in the DOM.
   */
  priceMasked: z.boolean().default(false),
  /** What the car is (M11) — brand/model/specs + a cover thumbnail for the card. */
  vehicle: lotVehicleSchema.nullable().optional(),
  // Take-control is public (bidders can see a lot may soft-close-extend); the numeric
  // config below is manager-only (like `reserve`) — absent for ordinary viewers.
  manualControl: z.boolean(),
  incrementOverride: z.number().int().nullable().optional(), // kuruş; fixed per-lot step
  postThresholdIncrement: z.number().int().nullable().optional(), // kuruş; post-reserve step
  softCloseMs: z.number().int().nullable().optional(), // ms; soft-close window
  currency: z.string(),
  startDate: z.number().int(), // epoch-ms
  endDate: z.number().int(), // epoch-ms
  state: lotStateSchema,
  currentTopBid: z.number().int().nullable(), // kuruş
  currentTopBidId: z.uuid().nullable(),
  bidCount: z.number().int(),
  winnerUserId: z.uuid().nullable(),
  // Per-lot conference room (M5, ADR 0018); null until provisioned. `hostUrl` is
  // manager-only (null for ordinary viewers); bidders get the attendee `joinUrl`.
  conference: conferenceInfoSchema.nullable().optional(),
});
export type SaleLot = z.infer<typeof saleLotSchema>;

/**
 * `GET /auctions/:id/lots/:lotId` (M11) — one lot plus the whole vehicle record, for the
 * bidder's vehicle-detail page. The lot carries the same masking as the sale detail; the
 * vehicle never carries the hidden reserve (ADR 0013), whoever is asking.
 */
export const lotDetailSchema = z.object({
  saleId: z.uuid(),
  saleTitle: z.string().nullable(),
  saleState: saleStateSchema,
  lot: saleLotSchema,
  // The bidder-facing projection, NOT the admin one — see `lotVehicleDetailSchema`.
  vehicle: lotVehicleDetailSchema,
});
export type LotDetail = z.infer<typeof lotDetailSchema>;

// --- List query (GET /admin/sales) ------------------------------------------

/** The lifecycle bucket behind the admin list's tabs (`?state=`). */
export const saleListBucketSchema = z.enum(['future', 'past', 'draft']);
export type SaleListBucket = z.infer<typeof saleListBucketSchema>;

/**
 * The {@link SaleState}s each bucket covers. Shared so the tab's server-side predicate and
 * the client's status-filter options cannot drift — a status option outside its tab would
 * silently return an empty page (the two filters AND, they don't replace one another).
 */
export const SALE_STATES_BY_BUCKET: Record<SaleListBucket, readonly SaleState[]> = {
  future: ['PUBLISHED', 'LIVE', 'PAUSED'],
  past: ['CLOSED', 'CANCELLED'],
  draft: ['DRAFT'],
};

/**
 * `?status=LIVE,PAUSED` — narrow the list to specific lifecycle states. Comma-separated
 * rather than a repeated key so a filtered list stays one copy-pasteable URL, and so the
 * value round-trips through the same `Record<string, string>` query bag as `q`/`limit`.
 */
const saleStatusFilterSchema = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean),
  )
  .pipe(z.array(saleStateSchema).min(1));

/**
 * `GET /admin/sales` query contract: the M10 search/pagination params plus the status and
 * start-date filters. `startFrom`/`startTo` are **inclusive epoch-ms bounds on `startDate`**
 * — the date the list itself shows — and are deliberately *not* refined against each other:
 * an inverted range is an empty page, not a 400, so a half-typed date pair in the admin's
 * two date inputs never replaces the table with an error banner.
 */
export const saleListQuerySchema = paginationQuerySchema.extend({
  state: saleListBucketSchema.optional(),
  q: z.string().optional(),
  status: saleStatusFilterSchema.optional(),
  startFrom: z.coerce.number().int().optional(), // epoch-ms, inclusive
  startTo: z.coerce.number().int().optional(), // epoch-ms, inclusive
});
export type SaleListQuery = z.infer<typeof saleListQuerySchema>;

export const saleSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string().nullable(),
  location: z.string().nullable(),
  state: saleStateSchema,
  // Presentation label (public callers see PAUSED masked to `inprogress`).
  presentationStatus: presentationStatusSchema,
  style: saleStyleSchema,
  listingDate: z.number().int(), // epoch-ms
  startDate: z.number().int(), // epoch-ms
  endDate: z.number().int(), // epoch-ms
  lotCount: z.number().int(),
});
export type SaleSummary = z.infer<typeof saleSummarySchema>;

export const saleDetailSchema = saleSummarySchema.extend({
  jointConference: z.boolean(),
  // The joint (sale-level) conference room (M5, ADR 0018); null for a per-lot conference or
  // until provisioned. `hostUrl` is manager-only. See each lot's `conference` for per-lot rooms.
  conference: conferenceInfoSchema.nullable().optional(),
  // The auction-level hidden-reserve default (ADR 0013); manager-only (like per-lot
  // `reserve`), absent for ordinary viewers. Editable pre-live via PATCH — surfaced here so
  // the admin form can show the current value.
  defaultReserve: z.number().int().nullable().optional(),
  // The auction barem (M4.5); manager-only (like per-lot `reserve`), absent for ordinary
  // viewers, and null when the sale uses a flat increment.
  incrementBands: incrementBandsSchema.nullable().optional(),
  targetCompany: z.array(z.uuid()),
  pausedAt: z.number().int().nullable(), // seconds remaining when paused
  pausedOnLotId: z.uuid().nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.number().int(), // epoch-ms
  cancelledAt: z.number().int().nullable(), // epoch-ms
  lots: z.array(saleLotSchema),
});
export type SaleDetail = z.infer<typeof saleDetailSchema>;

// --- Admin overview stats ---------------------------------------------------

/**
 * `GET /admin/sales/stats` — the counters behind the admin home ("Genel Bakış"). Every
 * figure is computed over the **same firm-scoped set of sales** the list endpoint would
 * return for the caller, so the overview can never total sales the operator may not open.
 *
 * Counts are grouped by lifecycle state rather than pre-bucketed into "active/passive": the
 * page decides which states read as active, and a raw per-state map survives a lifecycle
 * change without a contract change.
 */
export const saleStatsSchema = z.object({
  /** Sale counts keyed by {@link SaleState}; a state with no sales is `0`, never absent. */
  sales: z.record(saleStateSchema, z.number().int()),
  /** Lot counts keyed by {@link LotState}, over the same scoped sales. */
  lots: z.record(lotStateSchema, z.number().int()),
  /**
   * Realised revenue: one row per currency, because lots carry their own currency and
   * summing kuruş across currencies would invent a number. `amount` is the sum of the
   * winning bids (`currentTopBid`) of every SOLD lot in that currency.
   */
  sold: z.array(
    z.object({
      currency: z.string(),
      count: z.number().int(),
      amount: z.number().int(), // kuruş
    }),
  ),
});
export type SaleStats = z.infer<typeof saleStatsSchema>;

// --- Bid result -------------------------------------------------------------

export const bidSchema = z.object({
  id: z.uuid(),
  lotId: z.uuid(),
  bidderId: z.uuid(),
  amount: z.number().int(), // kuruş
  kind: bidKindSchema,
  placedAt: z.number().int(), // epoch-ms
  priorBidCount: z.number().int(),
});
export type Bid = z.infer<typeof bidSchema>;

/**
 * `{ bid, lot }` returned by the bid-placement endpoints (doc 04). `lot` carries only
 * the hot-path fields a client needs to re-render after a bid lands.
 */
export const bidResultSchema = z.object({
  bid: bidSchema,
  lot: z.object({
    id: z.uuid(),
    currentTopBid: z.number().int().nullable(), // kuruş
    currentTopBidId: z.uuid().nullable(),
    bidCount: z.number().int(),
    state: lotStateSchema,
  }),
});
export type BidResult = z.infer<typeof bidResultSchema>;
